// Firebase Auth client wrapper.
// Modular API (Firebase v9+); tree-shakeable.
//
// ADR-0065: CIP (Firebase Auth) is the credential authority. Email/password
// is a Firebase-managed provider — Firebase stores the hash, sends reset
// emails, and handles verification. We never see plaintext passwords past
// the network boundary and never run our own password machinery.
import { initializeApp, type FirebaseApp, type FirebaseOptions } from 'firebase/app';
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  applyActionCode,
  checkActionCode,
  verifyPasswordResetCode,
  confirmPasswordReset,
  reload,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  connectAuthEmulator,
  linkWithCredential,
  type AuthCredential,
  type AuthError,
  type Auth,
  type User,
} from 'firebase/auth';

let _app: FirebaseApp | null = null;
let _auth: Auth | null = null;

export interface InitializeFirebaseOptions extends FirebaseOptions {
  /**
   * When set, the Firebase Auth SDK is routed to the emulator at this URL
   * instead of Google's production auth servers. Local dev only — caller
   * is responsible for ensuring the emulator is running.
   *
   * Example: 'http://localhost:9099' (Firebase Auth emulator default).
   */
  authEmulatorUrl?: string;
}

export function initializeFirebase(config: InitializeFirebaseOptions): {
  app: FirebaseApp;
  auth: Auth;
} {
  if (_app && _auth) {
    // Idempotent for the same config; throw on a different config so a
    // staging-vs-prod misconfig fails loudly instead of silently using the
    // first one. apiKey + projectId together are sufficient for V1.
    if (_app.options.apiKey !== config.apiKey || _app.options.projectId !== config.projectId) {
      throw new Error(
        'initializeFirebase already initialized with a different config (apiKey/projectId mismatch)',
      );
    }
    return { app: _app, auth: _auth };
  }
  const { authEmulatorUrl, ...firebaseConfig } = config;
  _app = initializeApp(firebaseConfig);
  _auth = getAuth(_app);
  if (authEmulatorUrl) {
    // Routes the Auth SDK at the local emulator. disableWarnings silences
    // the orange "running on emulator" toast in the console.
    connectAuthEmulator(_auth, authEmulatorUrl, { disableWarnings: true });
  }
  return { app: _app, auth: _auth };
}

export function getFirebaseAuth(): Auth {
  if (!_auth) throw new Error('Firebase not initialized — call initializeFirebase first');
  return _auth;
}

/**
 * Thrown when `signInWithGoogle` (or any IdP popup) reports that an account
 * already exists for the email with a different sign-in method. Carries the
 * `pendingCredential` the caller can pass to `linkPendingCredential` after
 * the user proves account ownership via the existing method (typically
 * password sign-in).
 *
 * This replaces Firebase's default auto-merge-and-invalidate-password
 * behavior — see scripts/local/seed-firebase.cjs §"Re-set the password"
 * comment for the trap this avoids.
 */
export class AccountExistsWithDifferentCredentialError extends Error {
  readonly code = 'auth/account-exists-with-different-credential' as const;
  constructor(
    readonly email: string,
    readonly pendingCredential: AuthCredential,
  ) {
    super(`An account already exists for ${email} with a different sign-in method.`);
    this.name = 'AccountExistsWithDifferentCredentialError';
  }
}

export async function signInWithGoogle(
  customParameters?: Record<string, string>,
): Promise<{ user: User; idToken: string }> {
  const auth = getFirebaseAuth();
  const provider = new GoogleAuthProvider();
  if (customParameters) provider.setCustomParameters(customParameters);
  try {
    const result = await signInWithPopup(auth, provider);
    const idToken = await result.user.getIdToken();
    return { user: result.user, idToken };
  } catch (err) {
    if (
      err !== null &&
      typeof err === 'object' &&
      'code' in err &&
      (err as { code: unknown }).code === 'auth/account-exists-with-different-credential'
    ) {
      const email = (err as { customData?: { email?: string } }).customData?.email ?? '';
      const pendingCredential = GoogleAuthProvider.credentialFromError(err as AuthError);
      if (email && pendingCredential) {
        throw new AccountExistsWithDifferentCredentialError(email, pendingCredential);
      }
    }
    throw err;
  }
}

/**
 * Links a pending OAuth credential (captured from a prior
 * AccountExistsWithDifferentCredentialError) onto the currently signed-in
 * Firebase user. Caller must first sign the user in via the existing
 * method (password) so Firebase knows it's the same person.
 */
export async function linkPendingCredential(
  user: User,
  pendingCredential: AuthCredential,
): Promise<User> {
  const result = await linkWithCredential(user, pendingCredential);
  return result.user;
}

export async function signInWithEmail(
  email: string,
  password: string,
): Promise<{ user: User; idToken: string }> {
  const auth = getFirebaseAuth();
  const result = await signInWithEmailAndPassword(auth, email, password);
  const idToken = await result.user.getIdToken();
  return { user: result.user, idToken };
}

export async function signUpWithEmail(
  email: string,
  password: string,
): Promise<{ user: User; idToken: string }> {
  const auth = getFirebaseAuth();
  const result = await createUserWithEmailAndPassword(auth, email, password);
  // NO verification email is sent here.
  //
  // This used to fire Firebase's own sendEmailVerification() fire-and-forget,
  // which sent Firebase's DEFAULT-branded template. Verification mail is now
  // first-party: POST /api/v1/tenants/signup fires it server-side (see
  // modules/tenants/server/signup-route.ts's sendVerificationEmail port), from
  // HeyRoot's verified sending domain with HeyRoot's branding. Firebase still
  // owns the token and the act of verifying; only the envelope moved.
  //
  // Leaving the client-side call in place alongside the server-fired one would
  // send TWO verification emails for every password signup.
  const idToken = await result.user.getIdToken();
  return { user: result.user, idToken };
}

// sendPasswordReset() lived here and called Firebase's sendPasswordResetEmail(),
// which had CIP both MINT and MAIL the message -- so the user got CIP's default
// template carrying a <project>.firebaseapp.com link, the last flow still doing
// so. It moved to apps/backoffice/src/features/auth/request-password-reset.ts,
// which POSTs to POST /api/v1/auth/password-reset; the server generates the
// link, rewrites its host to ours and sends it through the platform sender.
//
// DELETED rather than deprecated in place (.claude/rules/deprecation-policy.md):
// a working client-SDK wrapper left exported is an invitation to re-adopt it,
// which would silently restore the firebaseapp.com link this replaced. This
// package stays a pure Firebase wrapper and gains no knowledge of the API origin.

// ── Email action codes (oobCode) ─────────────────────────────────────────────
//
// CIP mints an `oobCode` for verification, password reset and email recovery,
// and puts it behind a link that defaults to a <project>.firebaseapp.com host.
// root-api rewrites that host to a route WE own before mailing it (see
// apps/root-api/src/auth/action-link.ts), because the envelope being
// first-party is worth little if the link is not. The code and its semantics
// stay CIP's; only the page that redeems it moves.
//
// These wrappers exist so the redeeming route never imports `firebase/auth`
// directly -- this package stays the single Firebase boundary.

/**
 * Redeems a `verifyEmail` (or `recoverEmail`) action code. Idempotent from the
 * user's point of view only in the sense that a SECOND redemption throws
 * `auth/invalid-action-code` -- callers must treat that as "already used or
 * expired", not as a hard failure.
 */
export async function applyEmailActionCode(oobCode: string): Promise<void> {
  await applyActionCode(getFirebaseAuth(), oobCode);
}

/**
 * Inspects an action code without redeeming it. Used to tell the user WHICH
 * address is affected before they commit to the action.
 */
export async function checkEmailActionCode(
  oobCode: string,
): Promise<{ operation: string; email: string | null; previousEmail: string | null }> {
  const info = await checkActionCode(getFirebaseAuth(), oobCode);
  return {
    operation: info.operation,
    email: info.data.email ?? null,
    previousEmail: info.data.previousEmail ?? null,
  };
}

/**
 * Validates a `resetPassword` code and returns the account's email, so the
 * reset form can name the account it is about to change. Throws
 * `auth/invalid-action-code` / `auth/expired-action-code` on a spent or stale
 * link -- check this BEFORE rendering the password form, never after the user
 * has typed a new password.
 */
export async function verifyPasswordResetOobCode(oobCode: string): Promise<string> {
  return verifyPasswordResetCode(getFirebaseAuth(), oobCode);
}

/** Completes a password reset. The code is single-use. */
export async function confirmPasswordResetWithCode(
  oobCode: string,
  newPassword: string,
): Promise<void> {
  await confirmPasswordReset(getFirebaseAuth(), oobCode, newPassword);
}

// resendVerificationEmail lived here and called Firebase's
// sendEmailVerification(). It moved to
// apps/backoffice/src/features/auth/resend-verification.ts, which POSTs to the
// first-party endpoint instead, so a resend carries the same HeyRoot-branded
// body as the server-fired send at signup rather than Firebase's default
// template. This package stays a pure Firebase wrapper and gains no knowledge
// of the API origin.

/**
 * Reloads the user's Firebase Auth profile from the server (e.g. to pick up
 * `emailVerified` flipping to true after the user clicks the link Firebase
 * emailed them). Mutates the passed User in place; callers should re-read
 * `user.emailVerified` after this resolves, not before.
 */
export async function reloadCurrentUser(user: User): Promise<void> {
  await reload(user);
}

/**
 * Refreshes the locally cached Firebase user after a SERVER-SIDE change to
 * their account -- specifically `applyActionCode` flipping `emailVerified`.
 *
 * Without this the verification genuinely succeeds and the client still
 * believes the address is unverified until the next token refresh, so the
 * grace-window banner keeps nagging someone who just verified. The redemption
 * page is frequently opened in the SAME browser that holds the signup session,
 * which is exactly when the stale cache is visible.
 *
 * No-op when nobody is signed in -- the equally common case of opening the link
 * in a fresh browser or on another device. Callers should treat a rejection as
 * cosmetic and NOT downgrade a successful verification because of it.
 */
export async function refreshSignedInUser(): Promise<void> {
  const auth = getFirebaseAuth();
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) return;
  await reload(user);
}

export async function getCurrentIdToken(forceRefresh = false): Promise<string | null> {
  const auth = getFirebaseAuth();
  // Wait for Firebase's initial auth-state restore to settle before reading
  // currentUser. On a hard reload Firebase reads persisted state async, so
  // a caller firing in a top-level useEffect (TenantSwitcher, route loaders)
  // can observe currentUser === null even when the user IS signed in. That
  // path returned no Bearer header and the server 401'd. authStateReady()
  // resolves once on first settle and is a no-op on subsequent calls.
  await auth.authStateReady();
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken(forceRefresh);
}

export async function firebaseSignOutAndClear(): Promise<void> {
  const auth = getFirebaseAuth();
  await firebaseSignOut(auth);
}

export { onAuthStateChanged, type User, type Auth, type FirebaseApp, type FirebaseOptions };
